import { useQuery } from '@tanstack/react-query';
import { transformAlphaVantage } from '../utils/companyDataTransformers';

// To switch to FMP: swap the two fetch functions below and call transformFmp instead.
// The CompanyFundamentals component only consumes the normalized CompanyData shape
// returned by this hook — it never sees the raw API response.

const AV_BASE = 'https://www.alphavantage.co/query';
const AV_KEY = process.env.REACT_APP_ALPHA_VANTAGE_API_KEY;
const STALE = 60 * 60 * 1000; // 1 hour
/** Free-tier Alpha Vantage allows 1 request/second; keep a small buffer. */
const AV_REQUEST_STAGGER_MS = 1200;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function avThrottleMessage(data) {
  return data?.Information || data?.Note || data?.['Error Message'] || null;
}

async function fetchAVJson(functionName, symbol) {
  const url = `${AV_BASE}?function=${functionName}&symbol=${encodeURIComponent(symbol)}&apikey=${AV_KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${functionName} fetch failed`);
  const data = await res.json();
  const throttle = avThrottleMessage(data);
  if (throttle) {
    const err = new Error(throttle);
    err.isRateLimited = true;
    throw err;
  }
  return data;
}

async function fetchAVOverview(symbol) {
  const data = await fetchAVJson('OVERVIEW', symbol);
  if (!data?.Symbol) {
    const err = new Error(`No data found for ${symbol}`);
    err.notFound = true;
    throw err;
  }
  return data;
}

async function fetchAVEarnings(symbol) {
  const data = await fetchAVJson('EARNINGS', symbol);
  if (!Array.isArray(data?.quarterlyEarnings) && !Array.isArray(data?.annualEarnings)) {
    const err = new Error(`No earnings data found for ${symbol}`);
    err.notFound = true;
    throw err;
  }
  return data;
}

async function fetchAVCompanyData(symbol) {
  const overview = await fetchAVOverview(symbol);
  await sleep(AV_REQUEST_STAGGER_MS);
  const earnings = await fetchAVEarnings(symbol);
  return { overview, earnings };
}

export function useCompanyFundamentals(symbol) {
  const sym = symbol ? symbol.toUpperCase().trim() : '';

  const companyQuery = useQuery({
    queryKey: ['av-company-fundamentals', sym],
    queryFn: () => fetchAVCompanyData(sym),
    enabled: !!sym,
    staleTime: STALE,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      return Boolean(error?.isRateLimited);
    },
    retryDelay: (attempt) => 1500 * (attempt + 1),
  });

  const isLoading = companyQuery.isLoading;
  const isError = companyQuery.isError;
  const error = companyQuery.error;

  // Normalize to the standard CompanyData shape — component never sees raw AV fields
  const company = companyQuery.data
    ? transformAlphaVantage(companyQuery.data.overview, companyQuery.data.earnings)
    : null;

  return {
    company,
    isLoading,
    isError,
    error,
    companyLoading: isLoading,
  };
}
